import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  forwardRef,
  model,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/inputtextarea';
import { TranslateLangPipe } from '@gs-2.0/shared';

const TEXT_INPUT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TextInputComponent),
  multi: true,
};

const TEXT_INPUT_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => TextInputComponent),
  multi: true,
};

@Component({
  selector: 'app-text-input',
  standalone: true,
  imports: [InputTextModule, Textarea, FormsModule, TranslateLangPipe],
  providers: [TEXT_INPUT_VALUE_ACCESSOR, TEXT_INPUT_VALIDATOR],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent implements ControlValueAccessor, Validator {
  // Inputs
  label = input<string>('');
  placeholder = input<string>('');
  required = input<boolean>(false);
  inputType = input<'text' | 'email' | 'textarea' | 'url' | 'number'>('text');
  maxlength = input<number | null>(null);
  rows = input<number>(3);
  step = input<number | null>(null);
  min = input<number | null>(null);
  max = input<number | null>(null);
  errorMessages = input<Record<string, string>>({});
  /**
   * When true, the validation error (red border + message) is revealed as soon as the control
   * is `dirty || touched` — instead of the default blur + `validationDelay` reveal. Opt-in so
   * existing usages keep the delayed behavior; use it where the limit must be surfaced eagerly
   * (e.g. a max-length textarea whose over-limit state would otherwise disable Submit silently).
   */
  eagerError = input<boolean>(false);
  showCharacterCount = input<boolean>(false);
  /** @deprecated Use maxlength instead. This will be removed in a future version. */
  characterCountMax = input<number | null>(null);
  helperText = input<string | null>(null);
  validationDelay = input<number>(300);
  inputId = input<string>('');
  ariaLabel = input<string>('');

  // Outputs
  blur = output<void>();
  focus = output<void>();

  // Internal state
  value = model<string>('');
  isTouched = signal(false);
  showDelayedError = signal(false);
  disabled = signal(false);
  private validationTimer: any = null;
  private control: AbstractControl | null = null;
  // Bumped on every `validate()` run so the eager-error computed re-evaluates the control's
  // current validity/dirty/touched state (those live on AbstractControl, not on signals).
  private readonly controlStatusTick = signal(0);

  // Computed properties
  isInvalid = computed(() => {
    if (this.eagerError()) {
      this.controlStatusTick(); // re-evaluate whenever the control re-validates
      return this.hasErrors() && (this.control?.dirty === true || this.control?.touched === true);
    }
    return this.isTouched() && this.showDelayedError() && this.hasErrors();
  });

  displayHelperText = computed(() => {
    if (this.helperText()) return this.helperText();
    if (this.showCharacterCount() && this.maxlength()) {
      return 'validation.max_length';
    }
    return null;
  });

  effectiveAriaLabel = computed(() => this.ariaLabel() || this.label());

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Sync value changes to form control
    effect(() => {
      const currentValue = this.value();
      queueMicrotask(() => {
        this.onChange(currentValue);
      });

      // Reset error display when value changes and becomes valid
      if (this.control && !this.control.invalid) {
        this.showDelayedError.set(false);
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    // Notify the eager-error computed that validity/dirty/touched may have changed.
    this.controlStatusTick.update((tick) => tick + 1);
    return null;
  }

  // Event handlers
  onBlur(): void {
    this.isTouched.set(true);
    this.onTouched();
    this.blur.emit();

    // Start delayed validation
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.validationTimer = setTimeout(() => {
      this.showDelayedError.set(true);
    }, this.validationDelay());
  }

  onFocus(): void {
    // Clear any pending validation timer
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    // Reset error display to give user a clean slate
    this.showDelayedError.set(false);
    this.focus.emit();
  }

  // Helper methods
  hasErrors(): boolean {
    return this.control?.invalid ?? false;
  }

  getErrorKeys(): string[] {
    if (!this.control?.errors) return [];
    return Object.keys(this.control.errors);
  }

  getErrorMessage(errorKey: string): string {
    return this.errorMessages()[errorKey] || `validation.${errorKey}`;
  }

  getErrorParams(errorKey: string): Record<string, any> {
    if (!this.control?.errors) return {};
    const error = this.control.errors[errorKey];

    // Handle maxlength error specifically
    if (errorKey === 'maxlength' && typeof error === 'object') {
      return {
        requiredLength: error.requiredLength,
        actualLength: error.actualLength,
      };
    }

    return {};
  }
}
